import { Button, Icon, Tooltip } from '@deque/cauldron-react';
import { parseAsync } from 'json2csv';
import React, { useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { getAiTokenUsage, type AiTokenUsageEvent } from '~/common/api-client';
import { useAuthContext, type AuthUser } from '~/common/contexts/auth';
import { useGlobalToast } from '~/common/contexts/globalToast';
import { useProducts } from '~/common/contexts/products';
import styles from './AITokenUsageExportButton.css';

/**
 * Header column names for the AI usage CSV file.
 */
export enum AiUsageCsvColumnName {
  Name = 'User Full Name',
  Email = 'User Email Address',
  Subscription = 'Subscription',
  Action = 'Action',
  Date = 'Date/Time',
  CreditsUsed = 'AI Credits Used'
}

const exportCsv = (csvString: string): void => {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', 'ai-usage.csv');
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const AITokenUsageExportButton: FC = () => {
  const user = useAuthContext().user as AuthUser;
  const { t } = useTranslation();
  const { getProductBySlug } = useProducts();
  const { setContents } = useGlobalToast();
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const createCsvString = (events: AiTokenUsageEvent[]): Promise<string> => {
    return parseAsync(
      events.map(event => {
        const fullName = [event.user.first_name, event.user.last_name]
          .filter(n => n)
          .join(' ');

        return {
          [AiUsageCsvColumnName.Name]: fullName,
          [AiUsageCsvColumnName.Email]: event.user.email,

          [AiUsageCsvColumnName.Subscription]: getProductBySlug(
            event.system_name ?? ''
          )?.name,

          [AiUsageCsvColumnName.Action]: event.action_name,
          [AiUsageCsvColumnName.Date]: new Date(event.event_at).toISOString(),
          [AiUsageCsvColumnName.CreditsUsed]: event.incremented_by
        };
      }),
      {
        fields: Object.values(AiUsageCsvColumnName) // Needed so header row is still included when there is no data.
      }
    );
  };

  const handleExportButtonClick = async (): Promise<void> => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);

    try {
      const usageEvents = await getAiTokenUsage(user.token);
      const csvString = await createCsvString(usageEvents);
      exportCsv(csvString);

      setContents(
        t('AI credit usage CSV file exported successfully.'),
        'confirmation'
      );
    } catch {
      setContents(t('Error exporting AI credit usage to CSV file.'), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className={styles.container}>
      <Button
        ref={buttonRef}
        type="button"
        onClick={handleExportButtonClick}
        variant="secondary"
        thin
        disabled={isExporting}
        aria-busy={isExporting}
      >
        <Icon
          type="export-solid"
          className={isExporting ? styles.disabledIcon : undefined}
        />
        {t('Export AI usage data')}
      </Button>
      <Tooltip target={buttonRef} variant="info">
        {t('Export the last 60 days of AI credit usage data to a CSV file.')}
      </Tooltip>
    </div>
  );
};

export default AITokenUsageExportButton;
